import {
  buildCanvasActionCardBridgeScript,
  buildCanvasChatPromptBridgeScript,
  buildCanvasSrcDoc,
  type CanvasHostTheme,
  type CanvasPackage,
  type CanvasRenderContext,
} from '@littlebird/canvas';
import canvasRuntimeSource from '@littlebird/canvas/runtime-bundle';

import handRegular from '@/assets/fonts/Caveat/Caveat-Regular.woff2?inline';
import serifRegular from '@/assets/fonts/Meraki/Meraki-Regular.otf?inline';
import monoRegular from '@/assets/fonts/PPNeueMontrealMono-Regular.otf?inline';
import sansRegular from '@/assets/fonts/Sohne/Sohne-Buch.otf?inline';
import sansSemibold from '@/assets/fonts/Sohne/Sohne-Halbfett.otf?inline';
import sansBold from '@/assets/fonts/Sohne/Sohne-Kraftig.otf?inline';
import { SPRITE_EXTERNAL_LINK_SCRIPT } from '@/features/sprites/sprite-srcdoc';

const fontSources = {
  sansRegular,
  sansSemibold,
  sansBold,
  serifRegular,
  monoRegular,
  handRegular,
};

export const CANVAS_DOCUMENT_HEIGHT_MESSAGE =
  'littlebird-canvas-document-height';
export const CANVAS_DOCUMENT_MEASURE_MESSAGE =
  'littlebird-canvas-document-measure';
export const CANVAS_DOCUMENT_WHEEL_MESSAGE = 'littlebird-canvas-document-wheel';

type WheelAxis = 'x' | 'y';

export const getCanvasWheelDeltaForParent = (
  elements: Element[],
  axis: WheelAxis,
  delta: number
) => {
  if (delta === 0) return 0;

  const canConsume = elements.some((element) => {
    const style = window.getComputedStyle(element);
    const overflow = axis === 'x' ? style.overflowX : style.overflowY;
    if (!/^(auto|scroll|overlay)$/u.test(overflow)) return false;

    const position = axis === 'x' ? element.scrollLeft : element.scrollTop;
    const maximum =
      axis === 'x'
        ? element.scrollWidth - element.clientWidth
        : element.scrollHeight - element.clientHeight;
    if (maximum <= 0) return false;

    return delta < 0 ? position > 0 : position < maximum;
  });

  return canConsume ? 0 : delta;
};

export const getCanvasDocumentHeight = (canvasDocument: Document) => {
  const root = canvasDocument.getElementById('root');
  if (!root) return 1;

  const rootTop = root.getBoundingClientRect().top;
  const view = canvasDocument.defaultView;
  const rootPaddingBottom = view
    ? Number.parseFloat(view.getComputedStyle(root).paddingBottom) || 0
    : 0;
  const directChildrenBottom = Array.from(root.children).reduce(
    (bottom, child) => {
      const childRect = child.getBoundingClientRect();
      const marginBottom = view
        ? Number.parseFloat(view.getComputedStyle(child).marginBottom) || 0
        : 0;
      return Math.max(bottom, childRect.bottom + marginBottom);
    },
    rootTop
  );
  const descendantOverflowBottom = Array.from(
    root.querySelectorAll('*')
  ).reduce((bottom, descendant) => {
    const descendantRect = descendant.getBoundingClientRect();
    return Math.max(bottom, descendantRect.bottom);
  }, rootTop);
  const contentBottom = Math.max(
    directChildrenBottom,
    descendantOverflowBottom
  );

  return Math.max(1, Math.ceil(contentBottom - rootTop + rootPaddingBottom));
};

const buildCanvasDocumentBridgeScript = (bridgeCapability: string) => `
(() => {
  const capability = ${JSON.stringify(bridgeCapability)};
  const postToParent = window.parent.postMessage.bind(window.parent);
  const getCanvasWheelDeltaForParent = ${getCanvasWheelDeltaForParent.toString()};
  const getCanvasDocumentHeight = ${getCanvasDocumentHeight.toString()};
  document.currentScript?.remove();
  let resizeFrame = 0;

  const reportHeight = () => {
    resizeFrame = 0;
    const height = getCanvasDocumentHeight(document);
    postToParent(
      { type: '${CANVAS_DOCUMENT_HEIGHT_MESSAGE}', height, capability },
      '*'
    );
  };

  const scheduleHeightReport = () => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(reportHeight);
  };

  window.addEventListener('message', (event) => {
    if (
      event.source === window.parent &&
      event.data?.type === '${CANVAS_DOCUMENT_MEASURE_MESSAGE}'
    ) {
      scheduleHeightReport();
    }
  });

  window.addEventListener('DOMContentLoaded', () => {
    document.documentElement.style.background =
      'var(--lb-canvas-background-subtle)';
    document.body?.style.setProperty(
      'background',
      'var(--lb-canvas-background-subtle)',
      'important'
    );

    const layoutStyle = document.createElement('style');
    layoutStyle.textContent =
      '#root > * { width: 100% !important; }';
    document.head.append(layoutStyle);

    const observer = new ResizeObserver(scheduleHeightReport);
    observer.observe(document.documentElement);
    if (document.body) observer.observe(document.body);
    const root = document.getElementById('root');
    if (root) {
      observer.observe(root);
      Array.from(root.children).forEach((child) => observer.observe(child));
    }

    document.fonts?.ready.then(scheduleHeightReport);
    scheduleHeightReport();
  }, { once: true });

  window.addEventListener('wheel', (event) => {
    if (!event.isTrusted) return;

    const elements = event.composedPath().filter(
      (entry) => entry instanceof Element
    );
    const deltaX = getCanvasWheelDeltaForParent(elements, 'x', event.deltaX);
    const deltaY = getCanvasWheelDeltaForParent(elements, 'y', event.deltaY);
    if (deltaX === 0 && deltaY === 0) return;

    postToParent({
      type: '${CANVAS_DOCUMENT_WHEEL_MESSAGE}',
      deltaX,
      deltaY,
      deltaMode: event.deltaMode,
      capability
    }, '*');
  }, { passive: true });
})();
`;

type CanvasDocumentOptions = {
  actionCardCapability?: string;
  chatPromptCapability?: string;
  renderContext: CanvasRenderContext;
} & (
  | {
      documentMode: true;
      bridgeCapability: string;
    }
  | {
      documentMode?: false;
    }
);

export const buildCanvasDocument = (
  canvasPackage: CanvasPackage,
  hostTheme: CanvasHostTheme,
  options: CanvasDocumentOptions
): string => {
  const { renderContext } = options;
  return buildCanvasSrcDoc({
    canvasPackage,
    hostTheme,
    runtimeSource: canvasRuntimeSource,
    fontSources,
    headScript: [
      `globalThis.littlebirdCanvasRenderContext = '${renderContext}';`,
      SPRITE_EXTERNAL_LINK_SCRIPT,
      options.chatPromptCapability
        ? buildCanvasChatPromptBridgeScript({
            capability: options.chatPromptCapability,
          })
        : '',
      options.actionCardCapability
        ? buildCanvasActionCardBridgeScript(options.actionCardCapability)
        : '',
      options.documentMode
        ? buildCanvasDocumentBridgeScript(options.bridgeCapability)
        : '',
    ].join('\n'),
  });
};
